import { atom, useAtom } from "jotai";
import { debounce, noop } from "lodash";
import React, {
  MutableRefObject,
  Suspense,
  useContext,
  useRef,
  useState,
} from "react";
import { FormProvider } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";

import {
  AvailableIntegrationFragment,
  ConnectionBaseFragment,
  ConnectionBridgeBaseFragment,
  IntegrationAbility,
  IntegrationConfigurationFragment,
  SubscriptionStatus,
  useIntegrationConfigurationSuspenseQuery,
} from "@/apollo/types";
import { Alert, AlertIcon } from "@/components/elements/Alert";
import { ActionButton } from "@/components/elements/Button";
import { confirm } from "@/components/elements/Confirm";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import {
  SlideOver,
  SlideOverBody,
  SlideOverCloseButton,
  SlideOverFooter,
  SlideOverHeader,
} from "@/components/elements/SlideOver";
import { SectionHeading, TextMuted } from "@/components/elements/Typography";
import { ConnectorsLimitAlert } from "@/components/modules/PlanAlerts";
import {
  ConnectorOption as ConnectorOptionType,
  useConnectorOptions,
} from "@/features/connectors";
import {
  useConnectorAdditionValidationUpgradeDialog,
  useSubscriptionStatus,
  useWeldPlanLimits,
} from "@/features/subscription";
import cn from "@/helpers/classNames";
import { useCallbackRef } from "@/hooks/useCallbackRef";
import { useMountEffect } from "@/hooks/useMountEffect";
import {
  IntegrationLogo,
  useCreateConnection,
  useIntegrationDocumentationUrl,
  useIntegrationsMap,
} from "@/integrations";
import { useMixpanel } from "@/monitoring/mixpanel";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { useToast } from "@/providers/ToastProvider";
import {
  AbilityFilter,
  ConnectorOption,
  Connectors,
  SearchFilter,
} from "@/widgets/connectors/connectors-list";
import { RequestConnector } from "@/widgets/request-connector";
import { ExternalLinkIcon } from "@/components/icons/domain-icons";

import ConnectionForm, {
  AuthorizeMethod,
  ConnectionFormSubmitButton,
  FormModel,
  useConnectionForm,
} from "./ConnectionForm/ConnectionForm";

export const ConnectionSlideOverContext = React.createContext<{
  integration: AvailableIntegrationFragment | null;
  setIntegration: (integration: AvailableIntegrationFragment | null) => void;
  resetIntegration: () => void;
  isConfigurationDirty: boolean;
  setIsConfigurationDirty: (dirty: boolean) => void;
  initialFocusRef?: React.MutableRefObject<HTMLElement | null>;
}>({
  integration: null,
  setIntegration: noop,
  resetIntegration: noop,
  isConfigurationDirty: false,
  setIsConfigurationDirty: noop,
});

export function ConnectionSlideOverContextProvider({
  integrationId,
  abilityFilter,
  children,
  initialFocusRef,
}: Pick<NewConnectionProps, "abilityFilter"> & {
  integrationId?: string;
} & {
  children: React.ReactNode;
  initialFocusRef?: React.MutableRefObject<HTMLElement | null>;
}) {
  const integrationsRegistry = useIntegrationsMap(abilityFilter);
  const [integration, setIntegration] =
    React.useState<AvailableIntegrationFragment | null>(() => {
      // Preselect integration if provided
      if (integrationId) {
        return integrationsRegistry.get(integrationId) ?? null;
      }
      return null;
    });

  const [isConfigurationDirty, setIsConfigurationDirty] = React.useState(false);

  const resetIntegration = React.useCallback(() => {
    setIntegration(null);
  }, []);

  const contextValue = React.useMemo(() => {
    return {
      integration,
      setIntegration,
      resetIntegration,
      isConfigurationDirty,
      setIsConfigurationDirty,
      initialFocusRef,
    };
  }, [
    integration,
    resetIntegration,
    isConfigurationDirty,
    setIsConfigurationDirty,
    initialFocusRef,
  ]);

  return (
    <ConnectionSlideOverContext.Provider value={contextValue}>
      {children}
    </ConnectionSlideOverContext.Provider>
  );
}

type NewConnectionProps = React.ComponentProps<typeof Connectors>;

type SlideOverProps = Pick<
  React.ComponentProps<typeof SlideOver>,
  "show" | "onClose" | "size"
>;

type AbilityFilterProps = Pick<NewConnectionProps, "abilityFilter">;

type NewConnectionSlideOverProps = AbilityFilterProps & {
  integrationId?: string;
  resetOnClose?: boolean;
  onConnectionAdded?: (connection: ConnectionBaseFragment) => void;
  onExternalAuthLinkCreated?: (
    connectionBridge: ConnectionBridgeBaseFragment,
  ) => void;
  onConnectionFailed?: (error: Error) => void;
  onClose: () => void;
  size?: SlideOverProps["size"];
  enableSelectExistingConnection?: boolean;
  defaultAuthorizeMethod?: AuthorizeMethod;
};

export function NewConnectionSlideOver(
  props: NewConnectionSlideOverProps & SlideOverProps,
) {
  const mixpanel = useMixpanel();

  const handleConnectionAdded = (
    connection: ConnectionBaseFragment,
    usingExistingConnection: boolean = false,
  ) => {
    props.onConnectionAdded?.(connection);
    mixpanel.track("Connector Setup Succeeded", {
      integration_id: connection.integrationId,
      using_existing_connection: usingExistingConnection,
    });
    props.onClose();
  };

  const searchInputRef = useRef<HTMLInputElement>(null);

  const [isConfigurationDirty, setIsConfigurationDirty] = React.useState(false);

  return (
    <>
      <SlideOver
        show={props.show}
        size={props.size ?? "lg"}
        onClose={async () => {
          const onClose = () => {
            setIsConfigurationDirty(false);
            props.onClose();
          };

          if (isConfigurationDirty) {
            const confirmed = await confirm({
              title: "Unsaved Changes",
              message: "Are you sure you want to discard your changes?",
              type: "danger",
            });
            if (confirmed) {
              onClose();
            }
          } else {
            onClose();
          }
        }}
        // afterLeave={() => {
        //   if (props.resetOnClose) {
        //     resetIntegration();
        //   }
        // }}
        initialFocus={searchInputRef}
      >
        <SlideOverCloseButton />
        <ConnectionSlideOverContextProvider
          {...props}
          initialFocusRef={searchInputRef}
        >
          <NewConnectionSlideOverContent
            {...props}
            onConnectionAdded={handleConnectionAdded}
            onDirtyChange={setIsConfigurationDirty}
          />
        </ConnectionSlideOverContextProvider>
      </SlideOver>
    </>
  );
}

function NewConnectionSlideOverContent(
  props: Omit<NewConnectionSlideOverProps, "onConnectionAdded"> & {
    onDirtyChange: (isDirty: boolean) => void;
    onConnectionAdded: (
      connection: ConnectionBaseFragment,
      usingExistingConnection?: boolean,
    ) => void;
    onConnectionBridgeAdded?: (
      connectionBridge: ConnectionBridgeBaseFragment,
    ) => void;
  },
) {
  const { integration, resetIntegration } = useContext(
    ConnectionSlideOverContext,
  );
  const toast = useToast();
  const mixpanel = useMixpanel();

  const { loading, createConnection } = useCreateConnection({
    onSuccess(connection) {
      props.onConnectionAdded(connection);
    },
    onError(error, integrationId) {
      props.onConnectionFailed?.(error);
      toast("Connector not created", error.message, "error");
      mixpanel.track("Connector Setup Failed", {
        integration_id: integrationId,
      });
    },
  });

  return (
    <>
      {integration === null ? (
        <SelectConnectorView
          {...props}
          onConnectionAdded={(connection) => {
            props.onConnectionAdded(connection, true);
          }}
        />
      ) : (
        <Suspense
          fallback={
            <div className="flex justify-center p-8 pt-20">
              <div className="flex items-center gap-4">
                <LoadingSpinner />
                <span className="text-muted-foreground">
                  Loading connector configuration...
                </span>
              </div>
            </div>
          }
        >
          <ConfigureConnectorView
            integration={integration}
            onSubmit={(integration, data) => {
              return createConnection(integration, data);
            }}
            isLoading={loading}
            onCancel={() => resetIntegration()}
            onDirtyChange={props.onDirtyChange}
            onClose={props.onClose}
            defaultAuthorizeMethod={props.defaultAuthorizeMethod}
            onExternalAuthLinkCreated={props.onExternalAuthLinkCreated}
          />
        </Suspense>
      )}
    </>
  );
}

function SelectConnectorView(props: NewConnectionSlideOverProps) {
  const { setIntegration, initialFocusRef } = React.useContext(
    ConnectionSlideOverContext,
  );
  const [abilityFilter, setAbilityFilter] = useState(props.abilityFilter);

  const mixpanel = useMixpanel();
  const trackDebounced = useDebouncedCallback(mixpanel.track, 500);

  const dwh = useDataWarehouseContext();
  const options = useConnectorOptions({ dwIntegration: dwh.integration });

  const { validateConnectionAddition, validationDialog } =
    useConnectorAdditionValidationUpgradeDialog();

  const { status } = useSubscriptionStatus();

  const handleIntegrationSelected = (option: ConnectorOptionType) => {
    validateConnectionAddition(option.integration.id, () => {
      setIntegration(option.integration);
    });
  };
  const { isConnectorsLimitReached } = useWeldPlanLimits();
  return (
    <>
      <SlideOverHeader>Add Connection</SlideOverHeader>
      <Connectors
        options={options}
        enableSelectExistingConnection={props.enableSelectExistingConnection}
        disableCreateNewConnectionType={isConnectorsLimitReached}
        defaultAuthorizeMethod={props.defaultAuthorizeMethod}
        abilityFilter={abilityFilter}
        onAbilityFilterChange={(value) => setAbilityFilter(value ?? undefined)}
        // Return existing connection immediately
        onSelectConnection={props.onConnectionAdded}
        // Otherwise, open the connection configuration
        onSelectIntegration={handleIntegrationSelected}
        onOptionsFiltered={(filteredOptions, filters) => {
          if (filters.searchText.trim() === "") {
            return;
          }
          trackDebounced("Connection Options Filtered", {
            resultCount: filteredOptions.length,
            abilityFilter: filters.abilityFilter,
            searchText: filters.searchText.trim().toLowerCase(),
          });
        }}
      >
        {({ options }) => (
          <SlideOverBody className="flex flex-col overflow-hidden px-0">
            {isConnectorsLimitReached && (
              <div className="mb-4">
                <ConnectorsLimitAlert
                  onClickConnections={() => props.onClose()}
                />
              </div>
            )}
            <div className="flex flex-1 flex-col gap-3 overflow-hidden">
              <div className="flex gap-2 px-6 py-1">
                <div
                  className={cn("w-full grow", {
                    "max-w-xs": !props.abilityFilter,
                  })}
                >
                  <SearchFilter
                    ref={
                      initialFocusRef as
                        MutableRefObject<HTMLInputElement | null> | undefined
                    }
                  />
                </div>
                {props.abilityFilter === undefined && <AbilityFilter />}
              </div>
              <div className="flex flex-col gap-6 overflow-auto px-6 pt-1 pb-8">
                <div>
                  <SectionHeading className="mb-2">
                    All Connectors
                  </SectionHeading>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {options.map((option) => (
                      <ConnectorOption
                        key={option.value}
                        option={option}
                        showAbilityLabels={props.abilityFilter === undefined}
                        showPlanRequirement={
                          status === SubscriptionStatus.Freemium
                        }
                      />
                    ))}
                  </div>
                </div>
                <div className="my-8 flex flex-col items-center">
                  <TextMuted className="mb-2 block text-center">
                    Didn't find what you are looking for?
                  </TextMuted>
                  <RequestConnector
                    defaultValues={
                      props.abilityFilter ===
                      IntegrationAbility.ActivateDestination
                        ? { reverseEtl: true, elt: false }
                        : undefined
                    }
                  />
                </div>
              </div>
            </div>
            {validationDialog()}
          </SlideOverBody>
        )}
      </Connectors>
    </>
  );
}

function ConfigureConnectorView(props: {
  integration: AvailableIntegrationFragment;
  onSubmit: (
    integration: IntegrationConfigurationFragment,
    data: FormModel,
  ) => void;
  isLoading: boolean;
  onCancel: () => void;
  onClose: () => void;
  onDirtyChange: (isDirty: boolean) => void;
  defaultAuthorizeMethod?: AuthorizeMethod;
  onExternalAuthLinkCreated?: (
    connectionBridge: ConnectionBridgeBaseFragment,
  ) => void;
}) {
  const { data } = useIntegrationConfigurationSuspenseQuery({
    variables: {
      id: props.integration.aliasMetadata?.id || props.integration.id,
    },
  });
  const integrationConfig = data.integrationConfiguration;

  const mixpanel = useMixpanel();

  const { setConfig, getConfig } =
    useConnectionConfigCache<Record<string, any>>();
  const formMethods = useConnectionForm(integrationConfig, {
    defaultValues: getConfig(props.integration.id),
  });

  const { watch } = formMethods;
  const { isDirty } = formMethods.formState;

  const onDirtyChange = useCallbackRef(props.onDirtyChange);
  React.useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const setConnectionConfigDebounced = React.useMemo(() => {
    return debounce((integrationId: string, data: any) => {
      setConfig(integrationId, data);
    }, 200);
  }, [setConfig]);

  React.useEffect(() => {
    const subscription = watch((data) => {
      setConnectionConfigDebounced(props.integration.id, data);
    });
    return () => subscription.unsubscribe();
  }, [setConnectionConfigDebounced, props.integration.id, watch, setConfig]);

  const documentationUrl = useIntegrationDocumentationUrl(
    props.integration.id,
    false,
  );

  useMountEffect(() => {
    mixpanel.time_event("Connector Setup Succeeded");
    mixpanel.track("Connector Setup Form Viewed", {
      integration_id: props.integration.id,
    });
  });

  const [externalAuthLinkCreated, setExternalAuthLinkCreated] = useState(false);
  const [authorizeMethod, setAuthorizeMethod] = useState<AuthorizeMethod>(
    props.defaultAuthorizeMethod ?? AuthorizeMethod.Self,
  );

  return (
    <FormProvider {...formMethods}>
      <SlideOverHeader className="flex items-center">
        <div className="flex items-center space-x-2">
          <IntegrationLogo id={props.integration.id} className="h-5" />
          <span>{props.integration.name}</span>
        </div>
        {documentationUrl != null && (
          <a
            href={documentationUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              mixpanel.track("Connector Setup Documentation Viewed", {
                integration_id: props.integration.id,
              });
            }}
            className="ml-10 inline-flex items-center text-sm underline"
          >
            View setup guide
            <ExternalLinkIcon className="ml-1 size-4" />
          </a>
        )}
      </SlideOverHeader>
      <SlideOverBody>
        {props.integration.aliasMetadata && (
          <Alert status="info" className="mb-4">
            <AlertIcon />
            <div>
              The {props.integration.name} connector is available via{" "}
              <strong>{props.integration.aliasMetadata.name}</strong>. Please
              view the documentation for more information.
            </div>
          </Alert>
        )}
        <form
          id="integration-form"
          onSubmit={formMethods.handleSubmit((values) =>
            props.onSubmit(integrationConfig, values),
          )}
          className="flex h-full flex-col"
        >
          <ConnectionForm
            integration={integrationConfig}
            isLoading={props.isLoading}
            authorizeMethods={[AuthorizeMethod.Self, AuthorizeMethod.External]}
            onExternalAuthLinkCreated={(connectionBridge) => {
              props.onExternalAuthLinkCreated?.(connectionBridge);
              setExternalAuthLinkCreated(true);
            }}
            onAuthorizeMethodChanged={setAuthorizeMethod}
            defaultAuthorizeMethod={props.defaultAuthorizeMethod}
          />
        </form>
      </SlideOverBody>
      <SlideOverFooter>
        {externalAuthLinkCreated ? (
          <ActionButton
            colorScheme="secondary"
            variant="ghost"
            onClick={() => {
              props.onClose();
            }}
          >
            Close
          </ActionButton>
        ) : (
          <>
            <ActionButton
              colorScheme="secondary"
              variant="ghost"
              onClick={() => props.onCancel()}
            >
              Back
            </ActionButton>
            <ConnectionFormSubmitButton
              integration={integrationConfig}
              isLoading={props.isLoading}
              isDisabled={authorizeMethod === AuthorizeMethod.External}
            />
          </>
        )}
      </SlideOverFooter>
    </FormProvider>
  );
}

/**
 * Small cache to store connection configuration data
 * added by the user. This is used to prefill the form
 * when the user goes back to the connection configuration
 */
const connectionConfigCache = atom<Record<string, any>>({});

function useConnectionConfigCache<T>(): {
  setConfig: (integrationId: string, config: T) => void;
  getConfig: (integrationId: string) => T | undefined;
} {
  const [cache, setCache] = useAtom(connectionConfigCache);

  const setConfig = React.useCallback(
    (integrationId: string, config: T) => {
      setCache((prev) => ({ ...prev, [integrationId]: config }));
    },
    [setCache],
  );

  const getConfig = React.useCallback(
    (integrationId: string) => {
      return cache[integrationId];
    },
    [cache],
  );

  return { setConfig, getConfig };
}
